import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Box,
  Checkbox,
  Flex,
  HStack,
  type HTMLChakraProps,
  MenuRoot,
  MenuSeparator,
  PrimaryButton,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuPositioner as RawMenuPositioner,
  MenuTrigger as RawMenuTrigger,
  Spacer,
  TertiaryButton,
  Text,
  TextButton,
  Tooltip,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowDownToBracketRegular,
  FaArrowUpFromBracketRegular,
  FaBoltRegular,
  FaChevronDownRegular,
  FaEllipsisRegular,
  FaGearRegular,
  FaPlusRegular,
  FaSitemapRegular,
  FaTrashCanRegular,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { useLoadedIssuerOptions } from '~/components/bidding/common/IssuerSelector'
import { KomaRibbon } from '~/components/bidding/common/KomaRibbon'
import type { KomaSlot, KomaSlotState } from '~/components/bidding/common/types'
import { CompactAlert } from '~/components/common/CompactAlert'
import { Modal } from '~/components/common/Modal'
import { RichActionMenu } from '~/components/common/RichActionMenu'
import { iconSize } from '~/constants/iconSize'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useCsvImportLink } from '~/hooks/useCsvImportReturn'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useWorkflowCaseIdParam } from '~/hooks/workflowCase/useWorkflowCaseIdParam'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { downloadCsv } from '~/libs/downloadCsv'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { komaNumberToTime } from '~/models/komaToTime'
import { AreaFilterSummary } from '~/pages/SpotBidding/components/AreaFilterSummary'
// NOTE(local): 確認ステップで /admin/approval-tasks と同じ承認一覧を埋め込むため
// AdminApprovalTasks の共有コンポーネントを再利用する (= 動作確認用)。
import { FilterRail } from '~/pages/SpotBidding/components/FilterRail'
import { FilterToggleButton } from '~/pages/SpotBidding/components/FilterToggleButton'
import { SharedBidListFilterFields } from '~/pages/SpotBidding/components/SharedBidListFilterFields'
import { SidebarFilterHeader } from '~/pages/SpotBidding/components/SidebarFilterHeader'
import { SpotBidCreateModal } from '~/pages/SpotBidding/components/SpotBidCreateModal'
import { SpotBidEmptyState } from '~/pages/SpotBidding/components/SpotBidEmptyState'
import { SpotBidMenuItemContent } from '~/pages/SpotBidding/components/SpotBidMenuItemContent'
import { SpotBidTable } from '~/pages/SpotBidding/components/SpotBidTable'
import { FILTER_RAIL_WIDTH } from '~/pages/SpotBidding/constants/filterSidebar'
import { LIST_HEADER_HEIGHT } from '~/pages/SpotBidding/constants/listHeader'
import { SPOT_BID_TRANCHES_MOCK } from '~/pages/SpotBidding/fixtures/spotBidsMockSeed'
import { useBidCsvExportContext } from '~/pages/SpotBidding/hooks/useBidCsvExportContext'
import type { BulkWithdrawMenu } from '~/pages/SpotBidding/hooks/useBulkWithdrawControl'
import { useBulkWithdrawMenuView } from '~/pages/SpotBidding/hooks/useBulkWithdrawMenuView'
import type { BidCreateDisabledReason } from '~/pages/SpotBidding/models/bidCreateLockModal'
import { bidCreateLockModalBodyKey } from '~/pages/SpotBidding/models/bidCreateLockModal'
import type { NormalBidTabListFilters } from '~/pages/SpotBidding/models/bidListFilters'
import { normalBidsOfIssuerPosition } from '~/pages/SpotBidding/models/issuerPosition'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import { isSpotBeforeDeadline } from '~/pages/SpotBidding/models/marketPhase'
import {
  buildNormalBidCsv,
  normalBidCsvPriceQueries,
} from '~/pages/SpotBidding/models/normalBidCsv'
import {
  isAlwaysListedDraft,
  isInPageAreaScope,
  isInPageIssuerScope,
} from '~/pages/SpotBidding/models/pageIssuerScope'
import {
  EMPTY_SHARED_BID_LIST_FILTER,
  type SharedBidListFilter,
  isSharedBidListFilterActive,
  matchesSharedBidListFilter,
} from '~/pages/SpotBidding/models/sharedBidListFilter'
import {
  type SpotBidRow,
  isNonExpiredDraftSpotBid,
} from '~/pages/SpotBidding/models/spotBidRow'
import {
  type SpotBidStatusCategory,
  deriveSpotBidStatusCategory,
} from '~/pages/SpotBidding/models/spotBidStatusCategory'
import {
  buildPositionSlots,
  detectInversions,
  detectJepxLimitViolations,
  detectLimitViolations,
  isInSpotInversionPool,
} from '~/pages/SpotBidding/models/spotBidValidation'
import {
  type SpotBidCreateSubmission,
  translateSpotWriteError,
} from '~/pages/SpotBidding/models/spotBidWrite'
import {
  canCreateSpotBid,
  resolveClearAllState,
  resolveCsvDownloadState,
} from '~/pages/SpotBidding/models/spotMenuActionState'
import { buildSpotCsvImportPath } from '~/pages/SpotCsvImport/models/spotCsvImportPath'
import { routePaths } from '~/routes/routePaths'

// chakra v3.29 + React 18 types の既知不具合で、 props interface が
// `extends HTMLChakraProps<...>, UnstyledProp` 形の component
// (Menu 系) は型が `{ unstyled }` のみに collapse し、
// children / style / value 等を JSX で受け取れなくなる。 ecloud 由来の
// component をそのまま使い、 取りこぼした props を補う型で付け直す。

/**
 * トランシェ filter の「未選択」を表す共有インスタンス。
 *
 * `setTrancheFilter(new Set())` を毎回新しい Set で呼ぶと、 既に空でも参照が
 * 変わるため `filteredBids` の `useMemo` が再評価され、 それに依存する前へ/次へ
 * ナビの scroll effect が再実行される。 対象行が (何らかの理由で) filter 解除後も
 * 表示されない時、 この参照変化だけで effect が無限に再実行され続ける
 * (setState → re-render → 新しい空 Set → effect 再実行 → setState … のループ)。
 * 空集合は常にこの 1 インスタンスを使い、 React の Object.is 比較で
 * no-op になるようにする。 **このインスタンスを直接書き換えない** (state の
 * 「未選択」を表す共有 sentinel。 選択時は `onChange` が新しい Set を渡す)。
 */
const EMPTY_TRANCHE_FILTER = new Set<string>()
const EMPTY_SELECTED_AREAS = new Set<AreaCode>()
const NOOP = (): void => undefined

/**
 * ⋯ メニューは ecloud custom `Menu` ではなく chakra 素の Menu slot で組む
 * (= BlockBidTab / BlockBidTable / MasterTableActionBar と同じ許可済みパターン)。
 *
 * 理由: ecloud `Menu` の `content` 型は `{ icon, label, value, variant, disabled, onClick }`
 * 固定で **区切り線を差せず、 item ごとの `title` (無効理由 tooltip) も渡せない**。
 * design (`spot.html:1364-1392`) は通常入札の ⋯ にも区切り線を 2 本持ち、
 * design/CLAUDE.md も「⋯ 項目の無効理由は `title` (tooltip) に載せる」と定めている
 * ため、 DS 高レベル部品では表現できない。 raw slot の使用はこの ⋯ に封じ込め、
 * 見た目 (negative 項目の色 / divider) は DS token で揃える。
 * `MenuPositioner` を挟むのは positioner 無しだと popup が inline 展開されて
 * ヘッダを押し広げるため。
 *
 * 型は chakra v3 の d.ts collapse を cast で復元する (BlockBidTab と同一)。
 */
const MenuTrigger = RawMenuTrigger as React.FC<
  HTMLChakraProps<'button'> & { asChild?: boolean }
>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuPositioner = RawMenuPositioner as React.FC<
  HTMLChakraProps<'div'> & { children?: React.ReactNode }
>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & {
    value: string
    disabled?: boolean
    onSelect?: () => void
    onClick?: (e: MouseEvent) => void
    title?: string
  }
>

type Props = {
  memberBidDisabledReason?: string
  /** 札一覧 (= 親 page が GetBiddingSpotApprovalRequest response から変換した実データ)。 */
  bids: SpotBidRow[]
  /** 受渡日 (= 親 page から渡す。 phase 判定にも利用)。 */
  targetDate: Date
  phase: SpotMarketPhase
  /**
   * page-level エリアフィルタ (AC-BSP-019、 複数選択) の実効エリア集合
   * (= 0 件選択は呼び出し元で取引可能な全エリアに正規化済み)。 チャート
   * (AC-BSP-016a) だけが単一選択時に限定されるので、 本タブの一覧絞り込みは
   * 実効集合と未選択状態で一覧を絞る (区分不明の札は AC-BSP-111b の例外)。
   */
  selectedAreas: Set<AreaCode>
  /** 未正規化のエリア選択。1 件以上なら一覧内に AC-BSP-132 の状態を示す。 */
  rawSelectedAreas?: Set<AreaCode>
  /** AC-BSP-132 の「絞り込みを解除」。空選択 (= 全エリア) へ戻す。 */
  onClearAreaFilter?: () => void
  /** エリア未選択時の区分不明の札を出せる業務エリア (AC-BSP-111b)。 */
  unknownIssuerUnselectedAreas?: readonly AreaCode[]
  /**
   * チャートの対象エリア (= page の `singleSelectedArea`。 エリアフィルタで単一
   * エリアを選んでいる時だけ定まり、 複数・0 件選択では undefined。 AC-BSP-016a)。
   * 入札追加 modal のエリア・数量の既定値はこの 1 値でだけ決める。 本タブで
   * 正規化後の `selectedAreas` から組み直すと、 0 件選択かつ取引可能エリアが
   * 1 つの組織でチャート (ガイド表示) と modal (既定入力あり) が食い違う。
   */
  chartArea: AreaCode | undefined
  /**
   * 取引不可エリア (= 取引契約管理で取引可能に設定されていないエリア)。 札作成 modal の
   * エリア選択肢から除く (= 発注できないエリアの札を作らせない)。 取引可能エリアを
   * 取得できていない場合は空集合 = 制限しない (page の fallback と同じ扱い)。
   */
  disabledAreas: ReadonlySet<AreaCode>
  /**
   * 画面上部ツールバーで選択中の発注主体区分 (AC-BSP-005d)。 コマのクリックで開く
   * 札作成モーダルをこの区分で開く。
   */
  pageIssuerType: LicenseType
  /**
   * 入札一覧と違反バナーをスコープする発注主体区分 (AC-BSP-019 ※)。 ツールバーの区分を
   * page が求めた値で、 エリアフィルタの可否と同じ。 UNSPECIFIED の間は絞り込まない。
   * 渡し忘れで黙って無スコープになるのを型で防ぐため必須。
   */
  issuerScopeType: LicenseType
  /**
   * 業務エリア設定で許可された JEPX 対象エリア (AC-BSP-019 / `~/hooks/useBusinessAreaFilter`)。
   * 札作成 modal はこれ以外のエリアを選択肢に残して選べなくし「業務エリア対象外」 を添える
   * (AC-BSP-005b)。
   */
  areaCodes: readonly AreaCode[]
  /**
   * 当日受渡日に DRAFT request が存在するか (= 追加入札 AC-079 / 複数申請セット
   * 並存 AC-082 対応後の指標)。 個別行の編集・削除可否は `SpotBidTable` が
   * 行ごとの `approvalState === DRAFT` で判定するため、 このフラグは
   * 「入札を全削除」 (= DRAFT request 単位の一括削除) の活性判定にのみ使う。
   * 入札追加そのものは `phase` (= 締切前かどうか、 AC-079/AC-064) で判定する。
   */
  hasDraftRequest: boolean
  /**
   * ワークフローケースが完了済み / 削除済みで読み取り専用か
   * (workflow-back-bar-readonly AC-15)。 true のとき入札追加・行の編集/削除・
   * 全クリアを一括で無効化する (= 締切前かどうかとは独立した抑止条件)。
   */
  isReadonly: boolean
  /**
   * 入札一覧を承認対象の申請セット (PENDING) に絞り込み表示中で、 追加先の下書きが
   * 画面に無いか (= 親 index.tsx の isFocusedDraftUnavailable)。 true のとき入札追加を
   * 無効化し、 理由と解除方法 (サイドバーの「リセット」) を tooltip で示す。
   *
   * 絞り込み中の書込は fail-closed で中断される (SpotFocusedRequestUnavailableError) が、
   * 押してからエラーで知らせる形だと AC-BSP-079 [P0] の追加入札が予告なく不能になる。
   * 締切後 (AC-064) / 読み取り専用と同じく、 押下前に不能であることを示す。
   */
  isFocusedDraftUnavailable?: boolean
  /**
   * 承認フロー設定 (`GetApprovalFlowScreen`) が取得中、 または取得失敗のままか
   * (= 親 index.tsx の isApprovalFlowUnavailable、 AC-BSP-015)。 true のとき
   * 入札追加を無効化する (2026-09-16 ユーザー決定 1-B)。
   *
   * 個別作成・一括作成は `resolveCreateApprovalMode` で対象エリアの承認モードを
   * 解決してから Create/Update する。 設定を読めていない間にそれを行うと、
   * 「要承認」 へ推測で倒したまま札を確定してしまい、 backend 照合 (別 PR、
   * 決定 1-A) が拒否しうる組合せを黙って作る。 読めるまでは作成そのものを
   * 止め、 押してからエラーで知らせる形にしない (締切後 (AC-064) / 読み取り
   * 専用と同じ方針)。
   */
  approvalFlowUnavailable?: boolean
  /**
   * 入札一覧ヘッダ action group の承認申請ボタン (= 親 index.tsx renderMainActionButton)。
   * design `spot.html` の wizard-submit-btn 相当を 入札追加▽ の右に並べる。
   * 承認状態により null (= REJECTED / SUBMITTED 等はボタン非表示)。
   *
   * 引数は一覧に表示中の下書きの札 (承認申請・提出の対象、 AC-BSP-007)。 表示中の定義
   * (下の `filteredBids`) を持つこのタブが渡し、 親は押した時点でこの札を確定する。
   */
  headerActionSlot?: (
    visibleDraftBids: readonly SpotBidRow[],
  ) => React.ReactNode
  /** 左フィルターサイドバーを開いているか (#7449、タブ間で共有)。 */
  isSidebarOpen: boolean
  /** サイドバーの開閉を切り替える (#7449)。 */
  onToggleSidebar: () => void
  /**
   * ブロック入札タブと共有する売/買・ステータスの絞り込み (AC-BSP-019)。 page が持つ 1 つの値で、
   * 「クリア」 もこの値を戻す。 渡し忘れでタブ内に閉じた絞り込みに戻らないよう必須。
   */
  listFilter: SharedBidListFilter
  onListFilterChange: (next: SharedBidListFilter) => void
  /**
   * このタブ固有の絞り込み (アラートの「売買逆転のみ表示」・「違反のみ」)。 保持 (AC-SC-031) と CSV 取込の
   * 着地での戻し方を page がまとめて持つので、 値は page から受け取る。 変える時は変えた項目だけを渡す
   * (page が直前の値に重ねる)。
   */
  tabListFilters: NormalBidTabListFilters
  onTabListFiltersChange: (patch: Partial<NormalBidTabListFilters>) => void
  /** 札新規作成 modal を開く callback。 */
  onOpenBidCreateModal: () => void
  /** 一括作成 modal を開く callback (AC-005)。 */
  onOpenBulkCreateModal: () => void
  /** CSV import modal を開く callback。 */
  /** 札の行編集 (= 親が編集 modal を開く)。 */
  onEditBid?: (row: SpotBidRow) => void
  /**
   * 行クリックで札を開く (#5356)。 編集できない札も開く (表示専用) ので、
   * `isReadonly` でも渡す。
   */
  onOpenBid?: (row: SpotBidRow) => void
  /** 札の行削除 (= 親が削除確認 modal を開く)。 */
  onDeleteBid?: (row: SpotBidRow) => void
  /**
   * 失効した札の「複製」 (AC-BSP-011)。 失効は締切後にしか発生しないため
   * `isBeforeDeadline` ガードの対象外。 複製先は表示中の受渡日ではなく、 親
   * (index.tsx) が締切前で最も近い受渡日へ切り替えてから新規作成モーダルを開く。
   * 出してよいか (読み取り専用・承認フロー設定未解決・固定日の締切) は親が判定し、
   * 出せない時は渡さない。
   */
  onDuplicateBid?: (row: SpotBidRow) => void
  /**
   * 入札中の札の「取下」 (AC-BSP-057)。 締切後 / read-only では出さない判定を
   * 本 tab が行うので、 呼び出し元は常に渡してよい。
   */
  onWithdrawBid?: (row: SpotBidRow) => void
  /** 送信中の申請セットの「下書きに戻す」 (AC-BSP-117 / AC-BSP-129)。 判定は SpotBidTable が [[canReturnToDraft]] で行う。 */
  onReturnToDraft?: (row: SpotBidRow) => void
  /**
   * 取消済みの札からの「再入札」 (AC-BSP-081)。 取消と再入札は自動連結しない —
   * この callback は新しい札の作成画面を開くだけ。
   */
  onRebidBid?: (row: SpotBidRow) => void
  /**
   * コマ枠作成を永続化する callback (= 親が当日 request に RPC append)。
   * 未指定なら作成は no-op。
   */
  onCreateBid?: (values: SpotBidCreateSubmission) => Promise<void>
  /**
   * チェック中エリアをコマ別合算した need[] (= GetProcurementNeed 由来)。
   * undefined = need 取得エラー (needUnavailable と併用、 need=0 で描画)。
   */
  needByKoma?: readonly number[]
  /**
   * 共通ツールバーの区分がチャート対象エリアに計画を持たず need が無い (AC-BSP-102)。
   * need 由来の数量の既定値を出さない。
   */
  needAbsent?: boolean
  /**
   * コマクリックのハンドラを親 page へ登録する。
   *
   * ポジションチャートがタブの外へ出た (issue #6596 論点3) ため、 チャートからの
   * クリックは page 経由でしか届かない。 page は受け取ったハンドラを ref に保持し、
   * **通常入札タブを表示している間だけ**チャートへ繋ぐ。 unmount 時は `undefined`
   * が渡る。
   */
  onRegisterSlotClick?: (handler: ((index: number) => void) | undefined) => void
  /**
   * 入札全削除 modal を開く callback (機能詳細7)。 親が確認 modal (キャンセル /
   * 全削除) を開き、 引数の札 (= 押した時点で一覧に表示中の下書き) を一括削除する。
   * 確認 modal の件数と削除が同じ札を指すよう、 対象はここで確定して渡す。
   * `excludedCount` は絞り込みで表示されず対象外になった下書きの札の数。
   */
  onOpenClearAllModal?: (
    targets: readonly SpotBidRow[],
    excludedCount: number,
  ) => void
  /**
   * 入札一覧 accordion に張る ref (= 親が差戻し後再編集 (AC-056) 後に
   * scrollIntoView するための参照)。
   */
  bidListRef?: React.RefObject<HTMLDivElement>
  /**
   * 指定時、 入札一覧をこの申請セット (承認案件) の札だけに絞り込む (AC-056:
   * 差戻し後再編集の対象を札そのもので示す。 複数の差戻しが並存しても
   * 直す対象が一意に見える)。 他の filter と同じく「フィルタをリセット」 で解除する。
   */
  focusedApprovalRequestId?: string
  /**
   * 申請セット絞り込み中に一覧見出しへ出す注記 (= 呼び出し元が t() 済みの完成文字列)。
   * 絞り込みの契機が複数あり (AC-BSP-056 差戻し後再編集 / AC-BSP-094 承認前の札確認)、
   * 契機によって「何に絞り込んでいるか」 が変わるため呼び出し元が文言を決める
   * (index.tsx の FOCUSED_REQUEST_NOTE_KEY)。 ここに既定値は持たない (= 省略時は注記を
   * 出さない。 契機が増えた時に黙って別契機の文言が出ないようにする)。
   */
  focusedApprovalRequestNote?: string
  /** 申請セット絞り込みの解除 (= filter リセット時に親の状態も戻す)。 */
  onClearFocusedApprovalRequest?: () => void
  /**
   * ⋯ メニュー「入札中の札を一括で取下」 (AC-BSP-116) の状態と押した時の操作。 通常入札・
   * ブロック入札の合算で決まるので親が持つ。 渡さない間は項目を出さない。
   */
  bulkWithdraw?: BulkWithdrawMenu
}

/**
 * SpotBidding 通常入札 tab を design HTML dot-by-dot で再現したコンポーネント。
 *
 * design HTML: `design/bidding/spot.html` `#tab-create` panel。
 *
 * 主要構成:
 * 1. アラートグループ: 下書き復元 / 売買逆転 / 入札制限違反 / JEPX 入札条件
 * 2. 入札一覧: 左サイドバー (受渡日表示 + フィルター) + 右 accordion
 * 3. 48 コマリボン
 * 4. 札一覧テーブル
 * 5. bid-create-menu dropdown (個別 / 一括 / CSV / Phase 2 placeholder)
 *
 * 札一覧は親 page が GetBiddingSpotApprovalRequest response を変換した実データ
 * (`bids` prop)。 トランシェは backend 非提供のため未設定
 * (= トランシェ filter は空集合。 後続 Wave で連携)。
 *
 * 約定結果の KPI カード・エリア × 売買集計は約定結果タブに置き、 本タブには出さない
 * (AC-BSP-003 / AC-BSP-037)。
 */

export const NormalBidTab = ({
  memberBidDisabledReason,
  bids,
  targetDate,
  phase,
  selectedAreas,
  rawSelectedAreas = EMPTY_SELECTED_AREAS,
  onClearAreaFilter = NOOP,
  unknownIssuerUnselectedAreas,
  chartArea,
  disabledAreas,
  pageIssuerType,
  issuerScopeType,
  areaCodes,
  hasDraftRequest,
  isReadonly,
  isFocusedDraftUnavailable = false,
  approvalFlowUnavailable = false,
  headerActionSlot,
  isSidebarOpen,
  onToggleSidebar,
  listFilter,
  onListFilterChange,
  tabListFilters,
  onTabListFiltersChange,
  onOpenBidCreateModal,
  onOpenBulkCreateModal,
  onEditBid,
  onOpenBid,
  onDeleteBid,
  onDuplicateBid,
  onWithdrawBid,
  onReturnToDraft,
  onRebidBid,
  onCreateBid,
  needByKoma,
  needAbsent = false,
  onRegisterSlotClick,
  onOpenClearAllModal,
  bidListRef,
  focusedApprovalRequestId,
  focusedApprovalRequestNote,
  onClearFocusedApprovalRequest,
  bulkWithdraw,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const caseId = useWorkflowCaseIdParam()
  const { readonlyReason } = useWorkflowLockReasons()
  // 取込が終わったら今の画面 (受渡日などの query ごと) へ戻れるよう、 戻り先を付ける
  const normalCsvImportLink = useCsvImportLink(
    buildSpotCsvImportPath({ kind: 'normal' }, caseId),
  )
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()

  // ----- 札一覧 (= 親 page が GetBiddingSpotApprovalRequest response から変換した実データ) -----
  // 表示 / KPI / ポジション chart 用に mutable state に保持し、 bids prop が更新されたら
  // resync する (= 作成 / 編集 / 削除は親の RPC 永続化 → 再取得 → ここに反映される)。
  // TODO(rpc): 単発入札作成 RPC 配線 (#774) で append を実書込に置換する。
  const [allBids, setAllBids] = useState<SpotBidRow[]>(bids)
  useEffect(() => {
    setAllBids(bids)
  }, [bids])

  // 入札締切前かどうか (= AC-079: 締切前は既存札の状態に関わらず追加入札
  // 可能。 AC-064: 締切後は入札作成・編集 UI を無効化)。
  const isBeforeDeadline = isSpotBeforeDeadline(phase)

  // 札を作成できるか。 締切前 (AC-079/AC-064) かつワークフローケースが
  // 読み取り専用でない (workflow-back-bar-readonly AC-15) かつ、 承認フロー設定
  // (AC-BSP-015、 2026-09-16 ユーザー決定 1-B) が解決済みで、 承認前の札確認
  // (AC-BSP-094) で追加先の下書きが画面から隠れていないときのみ許可する。 失効した札の
  // 複製 (親 index.tsx の canDuplicateBid) も同じ述語を使う。
  const canCreateBid =
    memberBidDisabledReason === undefined &&
    canCreateSpotBid({
      isBeforeDeadline,
      isReadonly,
      approvalFlowUnavailable,
      isFocusedDraftUnavailable,
    })
  // 無効化理由は ワークフロー読み取り専用 (AC-15) / 締切後 (AC-064) / 承認フロー設定
  // 未解決 (AC-BSP-015) / 承認対象への絞り込み中 の 4 系統。 完了・削除済みが確定した
  // 読み取り専用 (readonlyReason) は開いた入口による不可で画面内の操作では解消しない
  // ため、 他の理由より先に単独で出す (AC-WFW-014)。 取得中・失敗中の fail-closed は
  // 従来どおり締切の次に 'readonly' とする (readonlyReason が無いので吹き出しは出さない)。
  // tooltip と slot click 時の理由 modal で共用する。
  const bidCreateDisabledReason: BidCreateDisabledReason =
    readonlyReason !== undefined
      ? 'readonly'
      : !isBeforeDeadline
        ? 'deadline'
        : isReadonly
          ? 'readonly'
          : approvalFlowUnavailable
            ? 'approvalFlowUnavailable'
            : 'focused'

  // 入札追加ボタンの吹き出し。 無効化理由ごとの文言で、 key は動的組み立てにせず実キーを
  // 書き出す (= i18n key の grep / 未使用検出を効かせる)。 ワークフロー取得中・失敗中の
  // 'readonly' は readonlyReason が無いので undefined (吹き出しを出さない)。
  const bidCreateTooltip: string | undefined =
    memberBidDisabledReason ??
    (bidCreateDisabledReason === 'readonly'
      ? readonlyReason
      : t(
          bidCreateDisabledReason === 'deadline'
            ? 'page:biddingSpot.bidCreateMenu.disabledTooltip'
            : bidCreateDisabledReason === 'approvalFlowUnavailable'
              ? 'page:biddingSpot.bidCreateMenu.approvalFlowUnavailableTooltip'
              : 'page:biddingSpot.bidCreateMenu.focusedTooltip',
        ))

  // ----- コマ選択 → 入札追加 modal -----
  // コマリボンのコマをクリックすると、 そのコマを初期値にした入札追加 modal を開く。
  const [bidModalSlot, setBidModalSlot] = useState<number | undefined>(
    undefined,
  )

  // 締切後にグラフ / コマをクリックした時に出す「理由付きモーダル」。
  const [showEditLockReason, setShowEditLockReason] = useState(false)

  // グラフ / コマリボンのクリック。 締切前は常に入札追加 modal を開く
  // (AC-079: 既存札の状態に関わらず追加入札可)。 締切後は入札不可の理由を modal で示す。
  const handleSlotClick = (index: number): void => {
    if (memberBidDisabledReason !== undefined) return
    if (canCreateBid) {
      setBidModalSlot(index)
    } else {
      setShowEditLockReason(true)
    }
  }

  /*
   * ポジションチャートは **タブの外** に出た (`SpotPositionChartPanel` /
   * issue #6596 論点3) 一方、 コマクリックで開く入札追加モーダルの state
   * (`bidModalSlot` / `showEditLockReason`) は本タブが持ったままにしてある。
   * モーダルは本タブの候補・制限・下書きに依存していて、 page へ上げると
   * 依存を丸ごと引き連れることになるため。
   *
   * そこで **ハンドラだけを page へ預ける**。 page はそれを ref に持ち、
   * 通常入札タブを表示している間だけチャートの `onSlotClick` に繋ぐ
   * (= ブロック入札タブ表示中はクリック無効。 #6596 Q2)。
   * unmount 時は undefined を渡して外す (`lazyMount` で本タブが落ちた後に
   * 古いハンドラが残るのを防ぐ)。
   */
  useEffect(() => {
    onRegisterSlotClick?.(handleSlotClick)
    return () => onRegisterSlotClick?.(undefined)
  })

  // ----- filter state (売/買・ステータスは page が持つ共有の `listFilter`) -----
  const [trancheFilter, setTrancheFilter] =
    useState<Set<string>>(EMPTY_TRANCHE_FILTER)
  // 売買逆転のみ・入札制限超過のみは page が持つ (props の `tabListFilters`)。
  const { inversionOnly, limitOnly } = tabListFilters
  const setInversionOnly = (next: boolean): void => {
    onTabListFiltersChange({ inversionOnly: next })
  }
  const setLimitOnly = (next: boolean): void => {
    onTabListFiltersChange({ limitOnly: next })
  }

  const isPost = phase === 'matched' || phase === 'completed'

  // 単一エリア選択時のみ、 そのエリアを入札追加 modal の prefill に使う
  // (`IntradayBidding` の `singleSelectedArea` と同じ判断)。 判定は page に一本化
  // する ([[chartArea]] 参照)。
  const singleSelectedArea = chartArea

  // 区分を選び直す・作り直すしかない下書きは、 区分とエリアの絞り込みに関わらず出す (AC-BSP-019)
  const loadedIssuerOptions = useLoadedIssuerOptions(targetDate)
  const isAlwaysListed = useCallback(
    (b: SpotBidRow): boolean =>
      isAlwaysListedDraft(
        {
          isDraft: b.approvalState === BiddingApprovalState.DRAFT,
          issuerType: b.issuerType,
          areas: [b.areaCode],
        },
        loadedIssuerOptions,
      ),
    [loadedIssuerOptions],
  )

  // 一覧に出せる札 (売/買・ステータス等の絞り込みの前)。 エリアと区分の範囲に入る札に、 範囲の
  // 例外として出す札 (エリア未選択時の区分不明の札・常に出す下書き) を足す。
  const isInListScope = useCallback(
    (b: SpotBidRow): boolean =>
      (isInPageAreaScope(
        b.areaCode,
        b.issuerType,
        selectedAreas,
        unknownIssuerUnselectedAreas,
      ) &&
        isInPageIssuerScope(b.issuerType, issuerScopeType)) ||
      isAlwaysListed(b),
    [
      selectedAreas,
      unknownIssuerUnselectedAreas,
      issuerScopeType,
      isAlwaysListed,
    ],
  )

  // ----- area filter applied -----
  // 札作成 modal の数量の既定値の母集団。 範囲の例外として出す札は一覧に出すだけで、 ここには
  // 足さない: 選んでいないエリアの札が既定値 (調達必要量 − 入札時カバー量) から引かれ、 共通
  // チャートの残量と食い違うため。
  const areaScopedBids = useMemo(
    () => allBids.filter((b) => selectedAreas.has(b.areaCode)),
    [allBids, selectedAreas],
  )
  // 入札制限違反・売買逆転の検出範囲。 選択エリアに、 一覧に出せる札のエリアを足す: 範囲の例外
  // として出す札の違反も、 同じエリアの札との売買逆転も、 一覧の行・バナーに出すため (design
  // `spot.html` の「アラートの N 件は、 いま一覧に出ている行と一致させる」)。
  const violationScopedBids = useMemo(() => {
    const areas = new Set(selectedAreas)
    for (const b of allBids) {
      if (isInListScope(b)) areas.add(b.areaCode)
    }
    return allBids.filter((b) => areas.has(b.areaCode))
  }, [allBids, selectedAreas, isInListScope])

  // ----- 入札制限違反 (= settings「入札制限」SPOT_NORMAL の実設定値ベース) -----
  const { limits: bidLimits } = useBidLimits(BidSegment.SPOT_NORMAL)
  const limitViolations = useMemo(
    () => detectLimitViolations(violationScopedBids, bidLimits),
    [violationScopedBids, bidLimits],
  )
  // 売買逆転の検知対象は AC-BSP-009 の状態の札だけ (下書き・予約中・承認待ち・差戻し・送信中・
  // 入札中)。 一覧のステータスフィルタには依らない。
  const inversions = useMemo(
    () =>
      detectInversions(
        violationScopedBids.filter((b) =>
          isInSpotInversionPool(b, !isBeforeDeadline),
        ),
      ),
    [violationScopedBids, isBeforeDeadline],
  )
  // ----- JEPX 制度条件違反バナー (AC-BSP-065) -----
  // AC-BSP-065 は判定元の AC-BSP-050 を参照するのみで area には言及しないため、
  // 上の 2 アラート (逆転・テナント入札制限) と違って `violationScopedBids` ではなく
  // `bids` (= 受渡日全体、 全エリア) を対象にする。 提出確認モーダルの違反判定
  // (AC-BSP-077、 index.tsx の hasJepxViolation) と同じ `bids` を通すので、
  // 一覧バナーとモーダルの判定母集合が食い違わない。
  //
  // ここだけ `allBids` (= `bids` prop を useEffect で 1 render 遅れて同期するローカル
  // state) ではなく `bids` prop を直接使う (codex-review 指摘、 PR #8017)。 受渡日切替
  // 直後は `bids` が新しい受渡日の値に更新済みでも `allBids` はまだ前の受渡日のまま
  // なので、 `allBids` を使うと切替直後の 1 render だけ前日の違反件数が出てしまう。
  const jepxLimitViolations = useMemo(
    () => detectJepxLimitViolations(bids),
    [bids],
  )
  // AC 文言の「{N} 件」は違反札の件数 (= bid 単位)。 1 札が価格・数量の両方で
  // 違反することがあるため、 種別ごとに複数件返す detectJepxLimitViolations の
  // 出力を bidId で重複排除してから数える。
  const jepxViolationBidCount = useMemo(
    () => new Set(jepxLimitViolations.map((v) => v.bidId)).size,
    [jepxLimitViolations],
  )
  const violationBidIds = useMemo(
    () => new Set(limitViolations.map((v) => v.bidId)),
    [limitViolations],
  )
  const inversionBidIds = useMemo(
    () => new Set(inversions.flatMap((inv) => inv.bidIds)),
    [inversions],
  )
  // 売買逆転は検知対象の状態の札どうしを区分をまたいで突き合わせる (AC-BSP-009)。
  // バナーの件数だけ、 一覧に出ている区分の札に掛かるものに揃える (design `spot.html` の
  // 「アラートの N 件は、 いま一覧に出ている行と一致させる」)。
  const issuerScopedBidIds = useMemo(
    () => new Set(allBids.filter(isInListScope).map((b) => b.id)),
    [allBids, isInListScope],
  )
  const inversionAlertCount = useMemo(
    () =>
      inversions.filter((inv) =>
        inv.bidIds.some((id) => issuerScopedBidIds.has(id)),
      ).length,
    [inversions, issuerScopedBidIds],
  )
  const limitViolationAlertCount = useMemo(
    () => limitViolations.filter((v) => issuerScopedBidIds.has(v.bidId)).length,
    [limitViolations, issuerScopedBidIds],
  )
  // 「売買逆転のみ表示」・「違反のみ」 は alert の中のチェックボックスなので、 alert を出していない間
  // (逆転・違反が 0 件) は一覧に掛けない (保存値は変えない)。 保存した ON のまま違反の無い受渡日を開くと、
  // 操作できる UI が無いのに全行が隠れ、 承認申請・提出の対象 (表示中の下書き) も 0 件になるため (#12745)。
  const isInversionOnlyApplied = inversionOnly && inversionAlertCount > 0
  const isLimitOnlyApplied = limitOnly && limitViolationAlertCount > 0

  // 行のステータス区分 (AC-BSP-047)。 ステータスフィルタ (AC-BSP-019a) と一覧の
  // バッジ (SpotBidTable) が同じ関数を使い、 選んだ区分と表示される区分を 1 つの
  // 締切判定で揃える (#8578)。 1 タブ = 1 受渡日なので、 締切判定は分単位で更新
  // される phase (= `calcSpotPhase` の締切境界 `spotDeadlineAt`) で足りる。
  const statusCategoryOf = useCallback(
    (b: SpotBidRow): SpotBidStatusCategory =>
      deriveSpotBidStatusCategory(b, !isBeforeDeadline),
    [isBeforeDeadline],
  )

  // ----- filter 適用後 rows -----
  // 発注主体区分のスコープ (下の `filteredBids`) より前の母集団。 エリア・売/買・ステータス等の
  // 絞り込みだけを掛けた札で、 範囲の例外として一覧に出す札は足さない。 札作成 modal の数量の
  // 既定値 (`positions` から求める 調達必要量 − 入札時カバー量) の母集団になる。 区分の絞り込みは
  // `positions` 側で、 調達必要量と同じツールバーの区分に掛ける (AC-BSP-102 ※)。 一覧の区分
  // スコープ (`issuerScopeType`) は申請セットの絞り込み中に外れるため、 ここでは使わない。
  // 一覧の絞り込み (エリア・発注主体区分を除く)。 区分スコープ前の母集団と一覧の両方に掛ける。
  const matchesListFilters = useCallback(
    (b: SpotBidRow): boolean => {
      // ブロック入札タブと共有する売/買・ステータス (AC-BSP-019)。 ステータスは SpotBidTable の
      // ステータス列と同じ締切判定で区分を出す。
      if (!matchesSharedBidListFilter(b, listFilter, !isBeforeDeadline))
        return false
      if (trancheFilter.size > 0) {
        if (b.trancheId === undefined || !trancheFilter.has(b.trancheId))
          return false
      }
      if (isInversionOnlyApplied && !inversionBidIds.has(b.id)) return false
      if (isLimitOnlyApplied && !violationBidIds.has(b.id)) return false
      // AC-056: 再編集した申請セットに絞り込む (= 差戻し後に直す対象の札だけを出す)。
      if (
        focusedApprovalRequestId !== undefined &&
        b.approvalRequestId !== focusedApprovalRequestId
      )
        return false
      return true
    },
    [
      listFilter,
      isBeforeDeadline,
      trancheFilter,
      isInversionOnlyApplied,
      isLimitOnlyApplied,
      inversionBidIds,
      violationBidIds,
      focusedApprovalRequestId,
    ],
  )
  const bidsBeforeIssuerScope = useMemo(
    () => areaScopedBids.filter(matchesListFilters),
    [areaScopedBids, matchesListFilters],
  )

  // 入札一覧に出ている行 (AC-BSP-019 ※)。 一覧・CSV・コマリボンはこれ。
  const filteredBids = useMemo(
    () => allBids.filter((b) => isInListScope(b) && matchesListFilters(b)),
    [allBids, isInListScope, matchesListFilters],
  )
  // 承認申請・直接提出と入札全削除の対象 = 一覧に表示中の下書きの札 (機能詳細 7)。 表示中の定義は上の
  // filteredBids が唯一の SoT で、 全削除側に同じ絞り込みを書き写さない。
  const visibleDraftBids = useMemo(
    () => filteredBids.filter(isNonExpiredDraftSpotBid),
    [filteredBids],
  )
  const hiddenDraftCount =
    allBids.filter(isNonExpiredDraftSpotBid).length - visibleDraftBids.length
  // 一括取下 (AC-BSP-116) の項目。 対象は page が共通ツールバーの範囲と両タブで共有する絞り込みから
  // 数える (タブ固有の絞り込みは掛けない) ので、 このタブは表示だけを持つ。
  const bulkWithdrawView = useBulkWithdrawMenuView(bulkWithdraw)

  /**
   * 一覧から札を隠す可能性のある filter を全解除する (`inversionOnly` は対象自体を
   * 隠さないので対象外。 「フィルタをリセット」 (`resetFilters`) から呼ぶ)。 共有の売/買・
   * ステータスも戻すので、 ブロック入札タブの絞り込みも同時に戻る (AC-BSP-019)。
   */
  const clearBidHidingFilters = (): void => {
    onListFilterChange(EMPTY_SHARED_BID_LIST_FILTER)
    // 常に同じ空 Set インスタンスを渡す (EMPTY_TRANCHE_FILTER のコメント参照)。
    setTrancheFilter(EMPTY_TRANCHE_FILTER)
    setLimitOnly(false)
    onClearFocusedApprovalRequest?.()
  }

  // 絞り込み対象の申請セットのうち、 エリアフィルタで画面から落ちている札の件数。
  // エリアフィルタ (AC-BSP-019) で選択エリアに跨る申請セットを
  // 「対象の札を確認」 (AC-BSP-094) / 「差戻し後再編集」 (AC-BSP-056) で絞り込むと、
  // 「対象の申請分のみ表示中」 と出ているのに選択エリア分しか見えない。 承認確認 modal は
  // 意図的にエリアフィルタを掛けず全札を対象にしている (= 非表示エリアの違反札を
  // 素通しさせない) ため、 数の非対称を注記で明示する。
  const focusedHiddenByAreaCount = useMemo(() => {
    if (focusedApprovalRequestId === undefined) return 0
    return allBids.filter(
      (b) =>
        b.approvalRequestId === focusedApprovalRequestId && !isInListScope(b),
    ).length
  }, [allBids, focusedApprovalRequestId, isInListScope])

  // 通常タブ自身の slot (= 札作成 modal の prefill 数量 = need − cover に使う。
  // 詳細は prefillVolumeMw)。 母集団は一覧の範囲の例外を足す前の札 ([[bidsBeforeIssuerScope]]) を、
  // need と同じツールバーの区分の札に絞ったもの (AC-BSP-102 ※。 need は区分の計画から求める)。
  const positions = useMemo(
    () =>
      buildPositionSlots(
        normalBidsOfIssuerPosition(bidsBeforeIssuerScope, pageIssuerType),
        needByKoma,
        !isBeforeDeadline,
      ),
    [bidsBeforeIssuerScope, pageIssuerType, needByKoma, isBeforeDeadline],
  )
  // ポジション chart / 充足率バッジ / エリアラベルは **タブの外** (`SpotPositionChartPanel`)
  // へ移した (2026-09-07 / issue #6596 論点3)。 合算 slot は page が組んで直接その
  // パネルへ渡すので、 本タブは chart 用の派生値を一切持たない。
  // `positions` は入札追加モーダルの prefill (残量) 用にここに残る。

  // ----- 48 コマリボン slot 構築 -----
  const ribbonSlots = useMemo<KomaSlot[]>(
    () => buildRibbonSlots(filteredBids, violationBidIds, inversionBidIds),
    [filteredBids, violationBidIds, inversionBidIds],
  )

  // ----- 札作成 → 当日 request に永続化 (= 親の RPC append callback 経由) -----
  // 旧 mock append (= ローカル allBids に行を足すだけ) を廃止し、 onCreateBid で
  // 永続化する。 永続後に bids prop が再取得され allBids が resync して 一覧 /
  // ポジション chart / KPI / 提出 modal の集計がすべて同じ実データを指す。
  // modal の `onCreated` に直接渡す (= fire-and-forget しない、 issue #3511)。
  // 以前は `void persistCreatedBid(values)` で投げっぱなしにしていたため、 modal 側が
  // 永続化の完了を待てず in-flight 中の再クリックで Create が 2 発飛んでいた。
  const persistCreatedBid = async (
    values: SpotBidCreateSubmission,
  ): Promise<void> => {
    if (memberBidDisabledReason !== undefined) return
    if (onCreateBid === undefined) return
    try {
      await onCreateBid(values)
      showSuccessToast({ title: t('page:biddingSpot.bidCreate.success') })
    } catch (err) {
      showErrorToast({ title: translateSpotWriteError(err, t) })
    }
  }

  // CSV は手数料自動考慮が ON の間、 保存済みの入札価格から手数料単価で逆算した希望価格 (backend の
  // 逆算値) を出す (機能詳細 9)。 ON で手数料単価を取得できない間 (AC-BSP-100) と、 手数料自動考慮の
  // 設定・会社情報の取得に失敗した間は出力できず、 押せない理由を項目の下に文で出す
  // (ツールチップだけにしない)。 設定・会社情報を取得している間は押せるままにし、 押された
  // 出力は届くまで待つ (useBidCsvExportContext。 ブロック入札タブと同じ)。
  const csvExport = useBidCsvExportContext()

  // 現在表示中 (= フィルタ適用後) の札一覧を client 側で CSV 化してダウンロードする
  // (機能詳細 9)。 export RPC は作らず、 表示中データから直接生成する。 取込と同じ 11 列 + 末尾の
  // ステータス列で、 下書き・承認待ちの札も落とさない ([[buildNormalBidCsv]])。
  // 出すのは押した時点で表示中の札 (取得を待つ間に一覧が変わっても、 押した時の内容を出す)。
  const handleDownloadCsv = (): void => {
    csvExport.requestExport({
      priceQueries: normalBidCsvPriceQueries(filteredBids),
      run: (inputs) => {
        const file = buildNormalBidCsv(filteredBids, {
          ...inputs,
          deliveryDate: targetDate,
          statusCategoryOf,
        })
        downloadCsv(file.filename, file.csv)
        showSuccessToast({
          title: t('page:biddingSpot.csvDownload.success', {
            count: filteredBids.length,
          }),
        })
      },
    })
  }

  const resetFilters = (): void => {
    clearBidHidingFilters()
    setInversionOnly(false)
  }

  // ⋯ メニューの無効化条件と、 その理由 tooltip (design/CLAUDE.md「⋯ 項目の
  // 無効理由は `title` (tooltip) に載せる」)。 ブロック入札タブと同じ形に揃える
  // (2026-08-31 デザイナー指摘「両方の UI スタイルも可能な限り揃えたい」)。
  // 状態の判定は models の純関数に置き、 全分岐を F1 で固定する
  // (spotMenuActionState.test.ts)。
  const csvDownloadState = resolveCsvDownloadState(filteredBids.length)
  const clearAllState = resolveClearAllState({
    bidCount: allBids.length,
    hasDraftRequest,
    hasVisibleDraft: visibleDraftBids.length > 0,
    isReadonly,
  })
  const csvDownloadDisabled =
    csvDownloadState !== 'active' || csvExport.blockedReason !== undefined
  const clearAllDisabled = clearAllState !== 'active'

  const csvDownloadTitle = (): string => {
    if (csvExport.blockedReason !== undefined) return csvExport.blockedReason
    return csvDownloadState === 'empty'
      ? t('page:biddingSpot.menuTooltip.csvDownloadEmpty')
      : t('page:biddingSpot.menuTooltip.csvDownloadActive')
  }
  const clearAllTitle = (): string => {
    if (clearAllState === 'readonly') return readonlyReason ?? ''
    if (clearAllState === 'empty')
      return t('page:biddingSpot.menuTooltip.clearAllEmpty')
    if (clearAllState === 'noDraft')
      return t('page:biddingSpot.menuTooltip.clearAllNoDraft')
    if (clearAllState === 'noVisibleDraft')
      return t('page:biddingSpot.menuTooltip.clearAllNoVisibleDraft')
    return t('page:biddingSpot.menuTooltip.clearAllActive')
  }

  const anyFilterActive =
    isSharedBidListFilterActive(listFilter) ||
    trancheFilter.size > 0 ||
    isInversionOnlyApplied ||
    isLimitOnlyApplied ||
    focusedApprovalRequestId !== undefined

  // 充足率 badge / accordion-trigger 背景色 (= design HTML cov-pos/warn/neg)

  return (
    <VStack align="stretch" gap={4}>
      {/* ========== アラートグループ ==========
          中身は全部条件付きなので、 出るものが 1 つも無い状態がふつうにある。
          その時この VStack は**高さ 0 のまま flex item として残る**ため、 親の
          `gap={4}` が上下に 1 回ずつ効いて **32px** になり、 他のブロック間
          (16px) と揃わない (デザイナー指摘 2026-08-28「入札一覧上部の目視余白を
          その上の余白に揃えたい」。 Chromium 実測 32px → 16px)。
          `:empty` の時だけ畳んで flex から外す (条件を 1 つずつ数え直すと、
          alert が増減するたびに条件式を直す必要があり必ず腐る)。

          [DS差異] DS `VStack` の recipe は常に `display: flex` を当てるので、
          空の時だけ `none` にするのはその上書きにあたる。 DS は「中身が無い時に
          畳む」 という状態を持たないため、 画面側で足す必要がある。 */}
      <VStack align="stretch" gap={2} _empty={{ display: 'none' }}>
        {/* 売買逆転アラート */}
        {inversionAlertCount > 0 && (
          <>
            <CompactAlert
              description={t('page:biddingSpot.alert.inversionBody')}
              status="warning"
              title={t('page:biddingSpot.alert.inversionTitle', {
                count: inversionAlertCount,
              })}
            />
            {/* 絞り込みのチェックは Alert の外 (直後) に置く (alert-placement.md §4: Alert の中に入力部品を置かない)。 */}
            {/* [8080 準拠] 前へ/次へナビは置かない。 対象行の強調と「売買逆転のみ表示」 の
                絞り込みで該当行だけを一覧に出す (8080 `bidding/spot.html` #inversion-alert、
                2026-08-18 デザイナー判断。 spec AC-BSP-009 の書き換えは #9557 で PdM に依頼中)。
                絞り込めば該当行は一覧に並ぶので、 1 件ずつ送る操作と位置表示 (1 / 2) は冗長。 */}
            <Checkbox
              checked={inversionOnly}
              onCheckedChange={(details: {
                checked: boolean | 'indeterminate'
              }) => setInversionOnly(details.checked === true)}
            >
              {t('page:biddingSpot.alert.inversionFilter')}
            </Checkbox>
          </>
        )}
        {/* 入札制限違反アラート。 この alert は設定「入札制限」(テナント実設定値) の
            違反。 JEPX 制度条件 (価格 0.01〜999 円/kWh・0.1 刻み等) の違反は種別が
            異なるため、 別の JEPX 入札条件バナー (下、 AC-BSP-065) と提出確認モーダル
            (detectJepxLimitViolations、 AC-BSP-077) で別表示する。 */}
        {limitViolationAlertCount > 0 && (
          <>
            <CompactAlert
              description={t('page:biddingSpot.alert.limitViolationBody')}
              status="error"
              title={t('page:biddingSpot.alert.limitViolationTitle', {
                count: limitViolationAlertCount,
              })}
            />
            <Checkbox
              checked={limitOnly}
              onCheckedChange={(details: {
                checked: boolean | 'indeterminate'
              }) => setLimitOnly(details.checked === true)}
            >
              {t('page:biddingSpot.alert.limitViolationFilter')}
            </Checkbox>
          </>
        )}
        {/* JEPX 入札条件バナー (AC-BSP-065)。 提出確認モーダルの違反ブロック
            (AC-BSP-077) とは別に表示する。 AC-BSP-065 は area に言及しないため
            上の 2 alert と異なり area filter を掛けない (受渡日全体が対象)。
            判定は detectJepxLimitViolations を提出確認モーダルと共用する (同じ
            判定関数を通すので、 一覧バナーとモーダルの違反判定母集合が食い違わない)。 */}
        {jepxViolationBidCount > 0 && (
          <CompactAlert
            description={t('page:biddingSpot.alert.jepxViolationBody')}
            status="error"
            title={t('page:biddingSpot.alert.jepxViolationTitle', {
              count: jepxViolationBidCount,
            })}
          />
        )}
      </VStack>

      {/* ========== 入札一覧: 2カラム grid (左=サイドバー 280px / 右=accordion) ==========
          design HTML `spot.html:952` `div.spot-bid-list-wrapper`:
            grid-template-columns: 280px minmax(0,1fr)

          [design 差異] design の areas は `'sidebar accordion' '. footer'` で、 2 行目に
          合算値の KPI フッターを敷く。 spec AC-BSP-003 が「表示しない」 に反転した
          (2026-09-09 判断、 issue #7573) ため、 実装は footer 行ごと持たず 1 行の
          2 カラムにする。 合算値は約定結果タブの KPI カード (AC-BSP-002a) /
          エリア×売買集計 (AC-BSP-002b) 側で確認する。 design 側の追従も同じ形で行う。
      */}
      <Box
        gap={0}
        // #7449 / 2026-09-15 デザイナー指摘: 閉じても**列は落とさない**。
        // 280px → 48px のレールに縮めるだけにして、 サイドバー列は常に在る
        // (完全に隠すと、 絞り込みが効いていること自体が画面から消える)。
        gridTemplateColumns={
          isSidebarOpen
            ? '280px minmax(0,1fr)'
            : `${FILTER_RAIL_WIDTH} minmax(0,1fr)`
        }
        // 行は 1 行だけ。 KPI フッターは AC-BSP-003 の反転 (#7573) で撤去され、
        // footer area ごと無くなった (上の [design 差異] コメント参照)。
        gridTemplateAreas="'sidebar accordion'"
        display="grid"
        minWidth={0}
      >
        {/* ===== 左: フィルターサイドバー =====
            design HTML `spot.html:954-966` `aside.bid-list-sidebar`。
            発注主体は画面上部のツールバーで選び、 入札一覧をスコープする (AC-BSP-019 ※) ので、
            ここにはフィルタを置かない。 */}
        {/* 2026-09-15 デザイナー指摘「完全に隠すのではなく side menu のように
            補足表示して開いたり畳めたりできるようにしたい」。 閉じている間は
            48px のレールに縮める (#7449 初版の「要素ごと描かない」 を置き換え)。

            初版が要素ごと消していたのは、 width 0 で残すと隣接 accordion と
            共有している 1px の枠線が筋として残るため。 レールは実体のある列で、
            開いている時と同じ辺の持ち方 (左・上・下を持ち右は accordion へ集約)
            をそのまま引き継ぐので、 その問題は起きない。 */}
        {/* `hidden` ではなく条件レンダリングにする。 `hidden` 属性は UA 既定の
            `display:none` に頼るだけなので、 後から `display` を書けば黙って
            効かなくなる。 同じ `gridArea` に 2 要素を置く形にもなる。 */}
        {!isSidebarOpen && (
          <Box
            gridArea="sidebar"
            background="Surface.primary"
            borderTopWidth="1px"
            borderTopColor="Border.lowEmphasis"
            borderBottomWidth="1px"
            borderBottomColor="Border.lowEmphasis"
            borderLeftWidth="1px"
            borderLeftColor="Border.lowEmphasis"
            borderTopLeftRadius="md"
            borderBottomLeftRadius="md"
          >
            <FilterRail
              hasActiveFilter={anyFilterActive}
              onExpand={onToggleSidebar}
            />
          </Box>
        )}
        {isSidebarOpen && (
          <Box
            gridArea="sidebar"
            background="Surface.primary"
            borderTopWidth="1px"
            borderTopColor="Border.lowEmphasis"
            borderBottomWidth="1px"
            borderBottomColor="Border.lowEmphasis"
            borderLeftWidth="1px"
            borderLeftColor="Border.lowEmphasis"
            borderTopLeftRadius="md"
            borderBottomLeftRadius="md"
            // 右辺の border は隣接する accordion (gridArea=accordion) の
            // borderLeft に集約する (= design spot.html の合成カードは共有辺 1px。
            // sidebar 側にも borderRight を置くと gap=0 grid で 2px の二重線になる)
          >
            {/* フィルターセクション (design `aside.bid-list-sidebar > .sidebar-section`)。
              見出し → 各フィルターの順 (2026-08-31 デザイナー指摘
              「見出しと項目の表示位置を 8080 に合わせる」)。
              padding は design の `--space-3` (12px)。 見出しの負 margin がこれを
              打ち消して下線を全幅に伸ばすので、 ここを変える時は
              SidebarFilterHeader も合わせる。 */}
            <Box padding={3}>
              <SidebarFilterHeader
                action={
                  <TextButton
                    disabled={!anyFilterActive}
                    onClick={resetFilters}
                    size="sm"
                  >
                    {t('page:biddingSpot.sidebar.filterReset')}
                  </TextButton>
                }
                leading={
                  <FilterToggleButton isOpen onToggle={onToggleSidebar} />
                }
              />
              <VStack align="stretch" gap={4} paddingTop={3}>
                {/* 「選択中の受渡日」 の読み取り表示は置かない (#10123)。 絞り込みが
                  できず、 画面上部のバーの受渡日と二重になるため
                  (enechain-labs/ebalance-prototype#560 で 8080 からも撤去済み)。 */}
                {/* 売/買・ステータス (AC-BSP-019 / AC-BSP-019a)。 ブロック入札タブと共有する。 */}
                <SharedBidListFilterFields
                  onChange={onListFilterChange}
                  value={listFilter}
                />

                {/* トランシェ multi-select — P3 で対応予定のため、それまで非表示にする。
                  関連 state / filter ロジック (trancheFilter) は P3 復活時にそのまま使えるよう残置。 */}
                {/* <VStack align="stretch" gap={1}>
                <Text color="Text.highEmphasis" fontSize="xs" fontWeight="medium">
                  {t('page:biddingSpot.sidebar.filterTranche')}
                </Text>
                <FilterMultiSelect
                  onChange={setTrancheFilter}
                  options={SPOT_BID_TRANCHES_MOCK.map((tr) => ({
                    value: tr.id,
                    label: tr.name,
                  }))}
                  selected={trancheFilter}
                />
              </VStack> */}
              </VStack>
            </Box>
          </Box>
        )}

        {/* ===== 右: 入札一覧 =====
            design `spot.html:968-970`。 header には「入札一覧 (N件)」タイトルのみ。
            フィルターは左サイドバーに移設済み。

            **アコーディオンにしない** (デザイナー指摘 2026-08-27「入札一覧のアコーディオン
            を外したい (8080 準拠)」)。 8080 は `bidding/spot.html:690-697` で
            `#spot-bid-list-accordion > summary::before { content: none }` /
            `cursor: default` / `list-style: none` と**開閉標と操作性を明示的に殺し**、
            summary に `border-bottom: 1px solid var(--border-low)` を引いている。
            同 690 行のコメントが理由を書いている:

              「入札一覧 / ブロック入札 は常時開きっぱなしで運用、 アコーディオン chevron は
               不要 + 下線をフィルター見出しと統一」

            8080 が `<details>` の器を残しているのは既存の grid / CSS を壊さないためで、
            **振る舞いとしては常時開いた見出し + 本文**。 実装側は DS の Accordion を
            使うと chevron と開閉が必ず付いてくるので、 器ごと素の Box + 見出しに置き換える。

            なお **入札後ポジションのアコーディオンは残す** (8080 の
            `.spot-position-chart` は `.ecl-accordion` のまま。 チャートは畳みたい要求がある)。 */}
        <Box
          ref={bidListRef}
          gridArea="accordion"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="none"
          borderTopRightRadius="md"
          // 右下の角丸は KPI フッターが持っていた (design `.bid-footer`)。 フッター
          // 撤去 (AC-BSP-003) で右列がこの要素だけになったのでここへ移す。
          borderBottomRightRadius="md"
          // カードの白は角丸を持つこの要素に載せる (8080 `.ecl-accordion`)。
          backgroundColor="Surface.primary"
          data-testid="spot-bid-list-card"
        >
          <Flex
            alignItems="center"
            flexShrink={0}
            gap={3}
            // 区切り線は見出し側に持つ。 8080 の
            // `#spot-bid-list-accordion > summary { border-bottom: 1px solid var(--border-low) }`
            // と同じで、 常時開いているので「閉じている間も線が残る」問題は起きない
            // (2026-08-25 の指摘は開閉できた頃の話)。
            height={LIST_HEADER_HEIGHT}
            borderBottomWidth="1px"
            // 高さは左のフィルター見出しと同じ 56px に揃える (= 2 カラムの上端が
            // 揃う。 8080 `spot.html:1254-1255` が `padding: space-2 space-4` +
            // 「内部 40px ボタン + 8px*2 = 56px」で同じ値に統一している)。
            // 縦 padding を space-3 (12px) のままにすると 40px ボタンで 64px になり、
            // 左の見出しから 8px 下がる (2026-08-31 デザイナー指摘)。
            borderBottomColor="Border.lowEmphasis"
            paddingX={4}
            paddingY={2}
          >
            <HStack flex={1} gap={3} width="100%" minWidth={0}>
              <VStack align="start" gap={0} minWidth={0}>
                <Text
                  textStyle="sectionTitle"
                  color="Text.highEmphasis"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingSpot.list.bidListTitle')}{' '}
                  <Text
                    as="span"
                    textStyle="captionNormal"
                    color="Text.midEmphasis"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {t('page:biddingSpot.list.countSuffix', {
                      count: filteredBids.length,
                    })}
                  </Text>
                </Text>
                <AreaFilterSummary
                  onClear={onClearAreaFilter}
                  selectedAreas={rawSelectedAreas}
                />
              </VStack>
              {/* 開閉トグルは**サイドバー見出しの左端**へ移した (2026-09-15
                  デザイナー指摘「折りたたむ導線は、 広げる導線と同じ場所に配置
                  して、 開閉を連打できるようにしたい」)。 一覧ヘッダに置いて
                  いた頃は、 開く導線 (レール) と閉じる導線が画面の左右に分かれて
                  いた。 → `FilterToggleButton` の JSDoc */}
              {/* 対象申請セットに絞り込んでいる旨 (AC-056 差戻し後再編集 /
                    AC-BSP-094 承認前の札確認)。 解除はサイドバーの「リセット」
                    (= 他 filter と同じ導線)。 */}
              {focusedApprovalRequestId !== undefined &&
                focusedApprovalRequestNote !== undefined && (
                  <Text
                    textStyle="captionNormal"
                    color="Text.midEmphasis"
                    whiteSpace="nowrap"
                  >
                    {focusedApprovalRequestNote}
                  </Text>
                )}
              {/* 絞り込み対象のうちエリアフィルタで隠れている札の件数。 注記本体とは
                    別の Text にする (= 「N 件表示中」 の文言そのものは契機で決まる固定文で、
                    こちらは表示状態に対する補足)。 */}
              {focusedHiddenByAreaCount > 0 && (
                <Text
                  textStyle="captionNormal"
                  color="Text.midEmphasis"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingSpot.list.focusedAreaHiddenNote', {
                    count: focusedHiddenByAreaCount,
                  })}
                </Text>
              )}
              <Spacer />
              {/* design `spot.html:1064-1132` spot-bid-action-group:
                    入札追加▽ + N 件の入札を承認申請 + ⋯ を accordion header 右端に並べる。
                    accordion trigger (= button) 内なので click 伝播を止めて開閉と干渉させない。 */}
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <HStack gap={2}>
                  {/* + 入札追加 ▾ (= design bid-create-menu)。 常時表示し、
                        入札締切後 (AC-064) のみ disabled + hover tooltip で締切理由を示す
                        (#3230)。 締切前は既存札の状態に関わらず追加入札できる (AC-079) ため、
                        DRAFT 以外の状態で disable する分岐は撤去済み。 非表示だと札作成の導線
                        ごと消えて迷子になるため、 disabled 表示 + tooltip で理由を示す方針を維持する。
                        高レベル `Menu` (= IntradayBidding と同形) を使い、 popup は portal +
                        positioner で overlay 表示する (= ヘッダの縦幅に影響しない / 旧 MenuContent
                        直書きは positioner 無しで inline 展開してヘッダを押し広げていた)。 */}
                  {canCreateBid ? (
                    /* design `spot.html` の `.ecl-split-btn` 準拠 (2026-08-24
                         デザイナー指摘)。 **主ボタン = 個別作成を即実行**、 右の caret から
                         その他の作成方法を開く。 個別作成が既定という業務実態に合わせた形で、
                         3 項目を対等に並べる dropdown ではない。
                         2 つのボタンの間に区切り線を入れる (= design の
                         `.ecl-split-btn-main` / `.ecl-split-btn-caret` の境界)。
                         区切り線は caret の左 border (文字色の 25%) + `marginLeft="-1px"` で主ボタンの右 border に
                         重ねて 1 本にする (design/ui-feasibility/components/split-button.md「dev 実装の現状」。
                         計画作成 = pages/ProcurementPlanList と同じ書き方。 文字色から作るので variant ごとに色を書かない)。 */
                    <HStack gap={0}>
                      <TertiaryButton
                        borderRightRadius={0}
                        onClick={() => onOpenBidCreateModal()}
                        size="md"
                      >
                        <FaPlusRegular boxSize={iconSize.labeledButton} />
                        {t('page:biddingSpot.wizard.stepCreate')}
                      </TertiaryButton>
                      <RichActionMenu
                        ariaLabel={t(
                          'page:biddingSpot.bidCreateMenu.moreLabel',
                        )}
                        items={[
                          {
                            value: 'bulk',
                            title: t('page:biddingSpot.bidCreateMenu.bulk'),
                            description: t(
                              'page:biddingSpot.bidCreateMenu.bulkDesc',
                            ),
                            icon: (
                              <FaBoltRegular boxSize={iconSize.labeledButton} />
                            ),
                            onClick: () => onOpenBulkCreateModal(),
                          },
                          {
                            value: 'csv',
                            title: t('page:biddingSpot.bidCreateMenu.csv'),
                            description: t(
                              'page:biddingSpot.bidCreateMenu.csvDesc',
                            ),
                            icon: (
                              <FaArrowUpFromBracketRegular
                                boxSize={iconSize.labeledButton}
                              />
                            ),
                            // spec spot-bidding.md AC-BSP-006: 子ページへ遷移する
                            // (モーダルではない)。 通常入札タブなので kind=normal。
                            onClick: () => {
                              void navigate(normalCsvImportLink)
                            },
                          },
                        ]}
                        trigger={
                          <TertiaryButton
                            marginLeft="-1px"
                            borderLeftWidth="1px"
                            borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
                            borderLeftRadius={0}
                            aria-label={t(
                              'page:biddingSpot.bidCreateMenu.moreLabel',
                            )}
                            paddingX={2}
                            size="md"
                            title={t(
                              'page:biddingSpot.bidCreateMenu.moreLabel',
                            )}
                          >
                            {/* caret は装飾 (開閉の読み上げ名は aria-label 側が持つ)。
                              8080 は 20px の svg chevron (`.ecl-split-btn-caret svg`、10/19 実測)。
                              [DS差異] design/CLAUDE.md の「caret は Solid」は
                              `@enechain/ecloud-icons` に FaChevronDownSolid が無いため
                              満たせず、 Regular を採る (MasterTableActionBar と同判断)。 */}
                            <FaChevronDownRegular
                              boxSize={iconSize.labeledButton}
                            />
                          </TertiaryButton>
                        }
                      />
                    </HStack>
                  ) : (
                    <Tooltip
                      content={bidCreateTooltip ?? ''}
                      isHidden={
                        bidCreateTooltip === undefined ||
                        bidCreateTooltip === ''
                      }
                      placement="top"
                      showArrow
                    >
                      {/* disabled 要素は pointer event を発火しないため span で包んで
                            hover を拾う (SubStepRow と同パターン)。 */}
                      <span>
                        {/* **器は活性時と同じ 2 枚**にする。 1 枚に畳むと締切をまたいだ
                            瞬間に区切り線が消え、 別部品に変わったように見える
                            (8080 `.ecl-split-btn.is-disabled` は 2 枚を残して両方を
                            薄くする)。 器が `span` (インライン) なので HStack も
                            inline-flex の span にする — 既定の div だと外側 span の
                            矩形がずれて tooltip が離れた位置に出る。

                            ラベルは活性時 (:1309) と同じ `wizard.stepCreate` を引く。
                            無効時だけ直書きにすると英語 locale で 1 つだけ和文が残る
                            (キーは main の #6975 で新設。 本 branch との merge で衝突した
                            のはこの行)。 */}
                        <HStack as="span" gap={0} display="inline-flex">
                          <TertiaryButton
                            borderRightRadius={0}
                            disabled
                            size="md"
                          >
                            <FaPlusRegular boxSize={iconSize.labeledButton} />
                            {t('page:biddingSpot.wizard.stepCreate')}
                          </TertiaryButton>
                          <TertiaryButton
                            marginLeft="-1px"
                            borderLeftWidth="1px"
                            borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
                            borderLeftRadius={0}
                            aria-label={t(
                              'page:biddingSpot.bidCreateMenu.moreLabel',
                            )}
                            disabled
                            paddingX={2}
                            size="md"
                          >
                            <FaChevronDownRegular
                              boxSize={iconSize.labeledButton}
                            />
                          </TertiaryButton>
                        </HStack>
                      </span>
                    </Tooltip>
                  )}
                  {/* N 件の入札を承認申請 (= design wizard-submit-btn)。 承認状態別の
                        メインアクションボタンを親から流す (= REJECTED / SUBMITTED は null)。 */}
                  {headerActionSlot?.(visibleDraftBids)}
                  {/* ⋯ (= design wizard-more-menu)。 popup は positioner + portal で
                      overlay 表示する。

                      **ブロック入札タブ (BlockBidTab) と同じ raw slot 構成にしている。**
                      DS の高レベル `Menu` は `content` 配列が
                      `{icon,label,value,variant,disabled,onClick}` 固定で、
                      **区切り線 (MenuSeparator) を差せず、 無効項目に理由 tooltip
                      (`title`) も渡せない**。 design (spot.html:1364-1392) は通常入札の
                      ⋯ にも区切り線を 2 本持っており、 ブロック側だけ区切り線がある
                      のは実装都合の差だった (2026-08-31 デザイナー指摘
                      「通常とブロックで違う / 両方のスタイルを揃えたい」)。
                      raw slot の使用はこの ⋯ に封じ込め、 見た目は DS token で揃える。 */}
                  <MenuRoot>
                    <MenuTrigger
                      aria-label={t('page:biddingSpot.menu.moreLabel')}
                      title={t('page:biddingSpot.menu.moreLabel')}
                    >
                      {/* トリガーの見た目は design/CLAUDE.md「⋯（kebab）メニュー」節:
                          `btn-tertiary btn-square` (枠あり) + DS の水平3点。
                          trigger 自体が button になるので中は `as="span"` で
                          見た目だけ借りる (button の入れ子を作らない)。 */}
                      {/* icon だけのボタンは 40×40 の正方形 (UI 辞書
                          dictionary/components.md §1 / 8080 `btn-square`)。 */}
                      <TertiaryButton
                        as="span"
                        width="40px"
                        minWidth="40px"
                        paddingX={0}
                        size="md"
                      >
                        <FaEllipsisRegular boxSize={iconSize.labeledButton} />
                      </TertiaryButton>
                    </MenuTrigger>
                    <MenuPositioner>
                      <MenuContent minWidth="240px">
                        <MenuItem
                          onClick={(e: MouseEvent) =>
                            openOrNavigate(
                              navigate,
                              routePaths.adminBidLimits.path,
                              e,
                            )
                          }
                          value="bid-limits"
                        >
                          <SpotBidMenuItemContent
                            Icon={FaGearRegular}
                            label={t('page:biddingSpot.menu.bidLimits')}
                          />
                        </MenuItem>
                        <MenuItem
                          onClick={(e: MouseEvent) =>
                            openOrNavigate(
                              navigate,
                              routePaths.adminApprovalFlow.path,
                              e,
                            )
                          }
                          value="approval-flow"
                        >
                          {/* [8080 差異] 8080 は丸チェックだが、 丸チェックの
                              Regular は lint で禁止 (状態アイコンは Solid) で、
                              Solid にすると「成功状態」に読める。 承認フローの
                              構造を表す sitemap へ寄せる。 */}
                          <SpotBidMenuItemContent
                            Icon={FaSitemapRegular}
                            label={t('page:biddingSpot.menu.approvalFlow')}
                          />
                        </MenuItem>
                        {/* 区切り 1: 「別画面へ離脱する設定導線」と「この画面の操作」を分ける
                            (design spot.html:1375)。 */}
                        <MenuSeparator />
                        {/* CSV ダウンロード (機能詳細 9)。 表示中の札一覧を client 側で
                            CSV 化する。 札 0 件のとき、 および手数料単価・手数料自動考慮の
                            設定・会社情報を取得できず出力できない間 (AC-BSP-100) は無効。 */}
                        <MenuItem
                          disabled={csvDownloadDisabled}
                          onSelect={handleDownloadCsv}
                          title={csvDownloadTitle()}
                          value="csv-export"
                        >
                          <SpotBidMenuItemContent
                            Icon={FaArrowDownToBracketRegular}
                            description={csvExport.blockedReason}
                            label={t('page:biddingSpot.menu.csvDownload')}
                          />
                        </MenuItem>
                        {/* 入札中の札を一括で取下 (AC-BSP-116)。 下書きを消す全削除とは別の操作なので、
                            CSV と全削除の間に区切り線で別グループにする (design spot.html:1505)。
                            対象は通常入札とブロック入札の合算で、 どちらのタブから押しても同じ。 */}
                        {bulkWithdraw !== undefined &&
                          bulkWithdrawView !== undefined && (
                            <>
                              <MenuSeparator />
                              <MenuItem
                                disabled={bulkWithdrawView.disabled}
                                onSelect={bulkWithdraw.onOpen}
                                title={bulkWithdrawView.title}
                                value="bulk-withdraw"
                              >
                                <SpotBidMenuItemContent
                                  Icon={FaXmarkRegular}
                                  description={bulkWithdrawView.description}
                                  label={t(
                                    'page:biddingSpot.bulkWithdraw.menu',
                                  )}
                                />
                              </MenuItem>
                            </>
                          )}
                        {/* 区切り 2: 破壊的操作を他から隔てる (design spot.html:1381)。 */}
                        <MenuSeparator />
                        {/* 入札を全削除 (機能詳細7)。 一覧に表示中の下書きの札だけが対象で、
                            表示中の下書きがあるときのみ有効
                            (AC-066/AC-082: 非 DRAFT request の札は対象外)。 */}
                        <MenuItem
                          color="Text.negativeHigh"
                          disabled={clearAllDisabled}
                          onSelect={() =>
                            onOpenClearAllModal?.(
                              visibleDraftBids,
                              hiddenDraftCount,
                            )
                          }
                          title={clearAllTitle()}
                          value="clear-all"
                        >
                          <SpotBidMenuItemContent
                            Icon={FaTrashCanRegular}
                            label={t('page:biddingSpot.menu.clearAll')}
                          />
                        </MenuItem>
                      </MenuContent>
                    </MenuPositioner>
                  </MenuRoot>
                </HStack>
              </Box>
            </HStack>
          </Flex>
          <Box padding={0}>
            {/* 48 コマリボン。
                  締切後にクリックした場合は handleSlotClick が入札不可の理由を
                  modal で示す (= 黙って無効化しない)。 */}
            <Box flexShrink={0} margin={3}>
              <KomaRibbon
                autoScrollToCurrent={false}
                currentSlotIndex={1}
                onSlotClick={handleSlotClick}
                selectedSlotIndex={bidModalSlot}
                slots={ribbonSlots}
              />
            </Box>

            {/* 手順ステップ表示 (入札追加 → 承認申請中 → 提出完了) は 2026-10-05 の PdM 判断で
                廃止した (#8312、 spec AC-BSP-010・§機能詳細 6)。 札ごとの進み具合はステータス列、
                承認の状態は承認状態バナーで読める。 操作 (入札追加▽ / 承認申請 / ⋯) は入札一覧の
                見出しにある。 */}
            {/* 札一覧の行単位の DRAFT 判定は SpotBidTable 側で行う
                  (AC-066/AC-082b: DRAFT request の札のみ編集・削除可)。 締切後
                  (AC-064) と読み取り専用のときは handler を渡さず、 メニュー項目を
                  disabled にする (行そのものは閲覧のため残す)。 約定処理中
                  (phase 'matching') は isPost=false で操作列が出るため、 isPost では
                  なく isBeforeDeadline で塞ぐ (issue #5349)。 */}
            {/* design `spot.html:1172` 「page scroll 化で縦 scroll は撤去、 横 scroll のみ
                  table-scroll に委譲」。 縦は page スクロールに任せ、 横は SpotBidTable 内の
                  overflowX に委譲する (= ここで overflow:auto を持つと二重スクロールになる)。 */}
            {/* 札一覧は承認状態に関わらず常に出す (承認申請しても承認案件一覧に
                  差し替えない。 design `bidding/spot.html` も `#spot-bid-body` を差し替えない)。 */}
            <Box>
              <SpotBidTable
                /* #5537: 売買逆転 (止まらない警告) と 入札制限違反 (送信が
                       止まる違反) は別系統で描き分ける。 まとめて 1 つの set に
                       畳むと 2 種が同色になり見分けられない。 */
                emptyContent={
                  allBids.length === 0 ? (
                    <SpotBidEmptyState
                      canCreate={canCreateBid}
                      onCreateBulk={() => onOpenBulkCreateModal()}
                      onCreateCsv={() => {
                        void navigate(normalCsvImportLink)
                      }}
                      onCreateSingle={() => onOpenBidCreateModal()}
                    />
                  ) : undefined
                }
                inversionBidIds={inversionBidIds}
                isBeforeDeadline={isBeforeDeadline}
                isPost={isPost}
                isWorkflowReadonly={isReadonly}
                limitViolationBidIds={violationBidIds}
                memberBidDisabledReason={memberBidDisabledReason}
                onDeleteBid={
                  isReadonly || !isBeforeDeadline ? undefined : onDeleteBid
                }
                onDuplicateBid={onDuplicateBid}
                onEditBid={
                  isReadonly || !isBeforeDeadline ? undefined : onEditBid
                }
                /* 取下 / 再入札 の行の状態による出し分けは SpotBidTable 内の
                   resolveSpotBidWithdrawAction に閉じ、 締切 / read-only も
                   ここで先に潰さない (同じ関数が見るため)。 再入札だけは、
                   申請セットに絞り込み中で追加先の下書きが無い間 (行でなく画面の
                   状態) をここで潰す。 複製 (index.tsx の canDuplicateBid) と同じ
                   扱い (AC-BSP-129)。 */
                onOpenBid={onOpenBid}
                onRebidBid={isFocusedDraftUnavailable ? undefined : onRebidBid}
                onReturnToDraft={onReturnToDraft}
                onWithdrawBid={onWithdrawBid}
                /* AC-BSP-020: 受渡日に札が 1 件も無い時だけ空状態と作成の導線を出す。
                   絞り込みで 0 件になった時は SpotBidTable 既定の「該当する札がありません」。
                   作成の 3 つの入口は見出し右の split button と同じハンドラ。 */
                rows={filteredBids}
                statusCategoryOf={statusCategoryOf}
                tranchesMaster={SPOT_BID_TRANCHES_MOCK}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* コマ選択 → 入札追加 modal (= design `#bid-new-modal`)。 発注主体区分の
          選択肢は表示中の受渡日の広域マスタから求める。 */}
      {bidModalSlot !== undefined && memberBidDisabledReason === undefined && (
        <SpotBidCreateModal
          areaCodes={areaCodes}
          defaultAreaCode={singleSelectedArea}
          defaultIssuerType={pageIssuerType}
          // 数量の既定値 (入札後残量) は単一エリアの時だけ出す (AC-BSP-016a)。
          // 複数エリア選択中は need が 0 (チャート対象エリアなし) で cover だけが
          // エリアをまたいで合算され、 チャートで排除した合算値が入ってしまう
          // (時間前入札 AC-BIN-002a と同じ扱い)。
          defaultVolumeMw={
            singleSelectedArea === undefined || needAbsent
              ? undefined
              : prefillVolumeMw(positions[bidModalSlot - 1])
          }
          deliveryDate={targetDate}
          disabledAreas={disabledAreas}
          onClose={() => setBidModalSlot(undefined)}
          onCreated={persistCreatedBid}
          slotIndex={bidModalSlot}
        />
      )}

      {/* 札を追加できない時にグラフ / コマをクリックした時の理由付きモーダル (AC-064)。
          黙って何も起きない UX を避け、 追加できない理由を明示する。 */}
      <Modal
        footer={
          <PrimaryButton onClick={() => setShowEditLockReason(false)}>
            {t('page:biddingSpot.editLockModal.close')}
          </PrimaryButton>
        }
        isOpen={showEditLockReason}
        onClose={() => setShowEditLockReason(false)}
        title={t('page:biddingSpot.editLockModal.title')}
      >
        {/* グラフ / コマリボン click 時の理由。 締切の本文 (AC-BSP-064) は締切後だけに出し、
            読み取り専用・絞り込み中・承認フロー設定未解決は締切とは別の理由を出す。 */}
        <Text>
          {bidCreateDisabledReason === 'readonly' &&
          readonlyReason !== undefined
            ? readonlyReason
            : t(bidCreateLockModalBodyKey(bidCreateDisabledReason))}
        </Text>
      </Modal>
    </VStack>
  )
}

// 調達必要量 − 入札時カバー量 (= 買い向きにまだ足りない量) を札作成 modal の prefill 数量にする。
// 入力・保持とも MW で粒度は 0.1 MW (= 制度刻みそのもの、 issue #4285) なので、 0.1 MW グリッドへ
// 丸めた MW を渡す。 0 以下は undefined にして数量欄を空のままにする。
// 入札後残量 (remainMw) は #9067 で調達必要量の向きにそろえた (売りのコマでもプラス = 残り) が、
// 札作成 modal は売買を prefill しないので、 ここは向きをそろえる前の need − cover のまま使う
// (売りのコマで買いの数量を既定値にしない)。
const prefillVolumeMw = (
  slot: { needMw: number; coverMw: number } | undefined,
): number | undefined => {
  if (slot === undefined) return undefined
  const shortfallMw = slot.needMw - slot.coverMw
  return shortfallMw > 0 ? Math.round(shortfallMw * 10) / 10 : undefined
}

// ----- helper: 48 コマリボン 描画用 slot 構築 -----

/**
 * 札一覧から 48 コマの koma slot 配列を組み立てる。
 *
 * design HTML `spot.html:818-825` の通り、 コマ単位の見た目は以下を独立に重ねる:
 *   - state: pre (= 未来コマ / 札なし) / open (= 札ありで未約定) / matched (= 札ありで約定済)
 *   - hasBid: 1 件以上の札あり → spot-has-bid (= info 系で薄く塗る)
 *   - hasInversion: 売買逆転含む → warning 下線
 *   - hasViolation: JEPX 制限違反含む → negative 下線 (= inversion より優先)
 *
 * スポット市場では締切後でもコマリボンはグレーにしない (= 時間前/intraday の
 * per-koma GC とは異なり、 スポットは 1 日 1 回の一括 GC)。 入札不可は青い info
 * バナー (= design `#spot-gc-passed-banner`) で表現するため、 ここは phase に依らず
 * per-koma 業務状態 (= 札有無 / 約定 / 逆転 / 違反) のみを保持する。
 */
const buildRibbonSlots = (
  bids: SpotBidRow[],
  violationBidIds: Set<string>,
  inversionBidIds: Set<string>,
): KomaSlot[] => {
  const countByKoma = new Map<number, number>()
  const matchedByKoma = new Set<number>()
  const violationByKoma = new Set<number>()
  const inversionByKoma = new Set<number>()
  for (const b of bids) {
    countByKoma.set(b.timeCode, (countByKoma.get(b.timeCode) ?? 0) + 1)
    if (b.execVolumeMwh !== undefined) matchedByKoma.add(b.timeCode)
    if (violationBidIds.has(b.id)) violationByKoma.add(b.timeCode)
    if (inversionBidIds.has(b.id)) inversionByKoma.add(b.timeCode)
  }
  return Array.from({ length: 48 }, (_, i) => {
    const index = i + 1
    const hasBid = countByKoma.has(index)
    const state: KomaSlotState = hasBid
      ? matchedByKoma.has(index)
        ? 'matched'
        : 'open'
      : 'pre'
    return {
      index,
      time: komaNumberToTime(index),
      count: countByKoma.get(index),
      state,
      hasBid,
      hasInversion: inversionByKoma.has(index),
      hasViolation: violationByKoma.has(index),
    }
  })
}
